import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useToast } from '../ui/Toast';

const PROVIDERS = [
  {
    id: 'openai',
    label: 'OpenAI',
    badge: null,
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    keyPlaceholder: 'sk-...',
    keyHint: 'Get your key at platform.openai.com',
    local: false,
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    badge: null,
    models: ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-3-5-sonnet-20241022'],
    keyPlaceholder: 'sk-ant-...',
    keyHint: 'Get your key at console.anthropic.com',
    local: false,
  },
  {
    id: 'google',
    label: 'Google Gemini',
    badge: 'Free tier',
    models: ['gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro'],
    keyPlaceholder: 'AIza...',
    keyHint: 'Get your key at aistudio.google.com (free tier available)',
    local: false,
  },
  {
    id: 'groq',
    label: 'Groq',
    badge: 'Free tier',
    models: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768', 'gemma2-9b-it'],
    keyPlaceholder: 'gsk_...',
    keyHint: 'Free tier at console.groq.com — fast inference',
    local: false,
  },
  {
    id: 'ollama',
    label: 'Ollama (Local)',
    badge: 'Free · Local',
    models: [],
    keyPlaceholder: '',
    keyHint: 'No API key needed. Install from ollama.com and run models locally.',
    local: true,
    defaultEndpoint: 'http://localhost:11434',
    endpointHint: 'Default: http://localhost:11434. Works when backend and Ollama are on the same machine.',
  },
  {
    id: 'lmstudio',
    label: 'LM Studio (Local)',
    badge: 'Free · Local',
    models: [],
    keyPlaceholder: 'lm-studio (optional)',
    keyHint: 'Download from lmstudio.ai and enable the local server.',
    local: true,
    defaultEndpoint: 'http://localhost:1234',
    endpointHint: 'Default: http://localhost:1234. Enable the local server in LM Studio settings.',
  },
];

const LOCAL_PROVIDERS = ['ollama', 'lmstudio'];

const AiSettings = () => {
  const toast = useToast();
  const [settings, setSettings] = useState({ provider: null, model: null, hasKey: false });
  const [form, setForm] = useState({
    provider: 'openai',
    apiKey: '',
    model: 'gpt-4o',
    endpoint: '',
    customModel: '',
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const selectedProvider = PROVIDERS.find(p => p.id === form.provider) || PROVIDERS[0];
  const isLocal = LOCAL_PROVIDERS.includes(form.provider);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/settings');
      if (data.success) setSettings(data.data);
    } catch (err) {
      if (err.response?.status !== 403) {
        console.error('AiSettings fetch:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Reset model list when provider changes
  useEffect(() => {
    const p = PROVIDERS.find(x => x.id === form.provider);
    if (p) {
      setAvailableModels(p.models);
      if (!isLocal && p.models.length > 0) {
        setForm(f => ({ ...f, model: p.models[0], customModel: '' }));
      } else {
        setForm(f => ({ ...f, model: '', customModel: '' }));
      }
    }
  }, [form.provider, isLocal]);

  const refreshModels = async () => {
    if (!settings.hasKey || settings.provider !== form.provider) {
      toast.info('Save your API key first, then refresh to fetch the latest model list.');
      return;
    }
    setRefreshingModels(true);
    try {
      const { data } = await api.get(`/ai/models?provider=${form.provider}`);
      if (data.success && data.models.length > 0) {
        setAvailableModels(data.models);
        toast.success(`Loaded ${data.models.length} models${data.live ? ' (live from API)' : ' (curated list)'}.`);
      }
    } catch (err) {
      toast.error('Failed to fetch models.');
    } finally {
      setRefreshingModels(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const effectiveModel = isLocal
      ? (form.customModel.trim() || form.model || 'llama3')
      : (form.model || selectedProvider.models[0]);

    if (!isLocal && !form.apiKey.trim()) {
      toast.warning('Please enter your API key.');
      return;
    }
    if (!effectiveModel) {
      toast.warning('Please specify a model name.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        provider: form.provider,
        model: effectiveModel,
      };
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (form.endpoint.trim()) payload.endpoint = form.endpoint.trim();

      const { data } = await api.post('/ai/settings', payload);
      if (data.success) {
        toast.success('AI settings saved! Your key is encrypted and stored securely.');
        setForm(f => ({ ...f, apiKey: '' }));
        fetchSettings();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save AI settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete('/ai/settings');
      toast.info('AI key removed.');
      setSettings({ provider: null, model: null, hasKey: false });
    } catch {
      toast.error('Failed to remove key.');
    }
  };

  if (loading) return null;

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center text-xl">🤖</div>
        <div>
          <h3 className="text-slate-200 font-semibold">AI Coach Settings</h3>
          <p className="text-xs text-slate-400">Cloud, local, or free-tier LLM — your trades stay private</p>
        </div>
        {settings.hasKey && (
          <span className="ml-auto text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded font-medium">
            ✓ Key configured
          </span>
        )}
      </div>

      {settings.hasKey && (
        <div className="mb-4 p-3 bg-slate-700/30 rounded-lg flex items-center justify-between">
          <div className="text-sm text-slate-300">
            <span className="font-medium capitalize">{settings.provider}</span> · {settings.model}
            {settings.isLocal && <span className="ml-2 text-xs text-emerald-400">local</span>}
          </div>
          <button onClick={handleRemove} className="text-xs text-red-400 hover:text-red-300 transition-colors">
            Remove
          </button>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Provider */}
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">Provider</label>
          <select
            value={form.provider}
            onChange={e => setForm(f => ({ ...f, provider: e.target.value, endpoint: '' }))}
            className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            {PROVIDERS.map(p => (
              <option key={p.id} value={p.id}>
                {p.label}{p.badge ? ` — ${p.badge}` : ''}
              </option>
            ))}
          </select>
          {selectedProvider.keyHint && (
            <p className="text-xs text-slate-500 mt-1">{selectedProvider.keyHint}</p>
          )}
        </div>

        {/* Endpoint (local providers only) */}
        {isLocal && (
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Server Endpoint <span className="text-slate-500">(optional, defaults shown)</span>
            </label>
            <input
              type="url"
              value={form.endpoint}
              onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))}
              placeholder={selectedProvider.defaultEndpoint}
              className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
            {selectedProvider.endpointHint && (
              <p className="text-xs text-slate-500 mt-1">{selectedProvider.endpointHint}</p>
            )}
          </div>
        )}

        {/* Model selector */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-slate-400">Model</label>
            {!isLocal && (
              <button
                type="button"
                onClick={refreshModels}
                disabled={refreshingModels}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50"
                title="Refresh model list from provider API"
              >
                <RefreshCw size={10} className={refreshingModels ? 'animate-spin' : ''} />
                Refresh
              </button>
            )}
          </div>

          {isLocal ? (
            <input
              type="text"
              value={form.customModel}
              onChange={e => setForm(f => ({ ...f, customModel: e.target.value }))}
              placeholder={form.provider === 'ollama' ? 'llama3, mistral, phi3, gemma...' : 'model-name from LM Studio'}
              className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            />
          ) : (
            <select
              value={form.model}
              onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
              className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            >
              {availableModels.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          )}
        </div>

        {/* API Key */}
        {!isLocal && (
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              API Key {settings.hasKey && settings.provider === form.provider && '(leave blank to keep existing)'}
            </label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={form.apiKey}
                onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
                placeholder={settings.hasKey && settings.provider === form.provider ? '••••••••••••••••' : selectedProvider.keyPlaceholder}
                className="w-full bg-slate-700 border border-slate-600 text-slate-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:border-purple-500"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 text-xs"
              >
                {showKey ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              🔒 Encrypted with AES-256-GCM before storage. Never sent to third parties.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? 'Saving...' : settings.hasKey ? 'Update Settings' : 'Save & Connect'}
        </button>
      </form>
    </div>
  );
};

export default AiSettings;
